<template>
    <BaseDialog
        v-model:open="isOpen"
        title="Read a PDF or image"
        description="Extract content from a PDF or image for your conversation with Nadia."
        show-close
        :reserve-mobile-nav-space="false"
        dialog-content-class="fs-mask">
        <div class="flex flex-col gap-4">
            <div class="flex justify-center">
                <div class="flex items-center gap-2">
                    <BaseChip size="2xs">Beta</BaseChip>
                    <BasePopover v-if="allowAdvanced" as-child side="bottom" align="center" :side-offset="6" :collision-padding="12" content-class="w-80 p-4 sm:w-96">
                        <template #trigger>
                            <BaseButton theme="tertiary" size="sm" icon-only class="text-ink-muted" aria-label="Show an example visual-document flow">
                                <i class="bi bi-question-circle" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>
                            <div class="flex flex-col gap-3">
                                <p class="body-small-semibold">Example with advanced options</p>
                                <img
                                    :src="visualDocumentTutorial"
                                    alt="Animated example showing an image selected, a focus prompt entered, and a summary added to chat"
                                    class="w-full rounded-lgr border border-control-border" />
                                <p class="details text-ink-muted">Choose a file, let Nadia check and read it, then review the result before adding it to chat.</p>
                            </div>
                        </template>
                    </BasePopover>
                </div>
            </div>

            <ol v-if="isAdvanced" aria-label="Visual document analysis progress" class="grid grid-cols-3">
                <li
                    v-for="step in progressSteps"
                    :key="step.id"
                    class="relative flex min-w-0 flex-col items-center gap-2 px-1 text-center"
                    :aria-current="currentStage === step.id ? 'step' : undefined">
                    <span
                        v-if="step.id > 1"
                        aria-hidden="true"
                        class="absolute top-3.5 right-[calc(50%+0.875rem)] h-px w-[calc(100%-1.75rem)]"
                        :class="currentStage >= step.id ? 'bg-button-primary' : 'bg-control-border'"></span>
                    <button
                        type="button"
                        class="relative flex flex-col items-center gap-2 disabled:cursor-default"
                        :disabled="!canNavigateToStep(step.id)"
                        :aria-label="canNavigateToStep(step.id) ? `Return to ${step.label}` : undefined"
                        @click="navigateToStep(step.id)">
                        <span
                            class="body-small-semibold flex size-7 items-center justify-center rounded-full"
                            :class="
                                currentStage === step.id
                                    ? 'bg-button-primary text-button-primary-text'
                                    : currentStage > step.id
                                      ? 'bg-surface-sunken text-ink'
                                      : 'border border-control-border bg-control-bg text-ink-muted'
                            ">
                            {{ step.id }}
                        </span>
                        <span class="details-big whitespace-nowrap" :class="currentStage === step.id ? 'text-ink' : 'text-ink-muted'">
                            {{ step.label }}
                        </span>
                    </button>
                </li>
            </ol>

            <div aria-live="polite" aria-atomic="true">
                <p v-if="isLoadingCapabilities" class="body-small text-ink-muted">Loading document options…</p>
                <BaseNotification v-else-if="capabilitiesError" theme="error" hide-dismiss>
                    <template #content>
                        <div class="flex flex-col items-start gap-3">
                            <p class="body-small-semibold">Visual document analysis is unavailable. Try again in a moment.</p>
                            <BaseButton theme="secondary" size="sm" @click="fetchCapabilities()">Try again</BaseButton>
                        </div>
                    </template>
                </BaseNotification>
            </div>

            <div
                v-if="capabilities && !capabilitiesError && !isLoadingCapabilities && !inspection && !isInspecting"
                ref="fileDropZone"
                data-testid="work-document-dropzone"
                class="flex flex-col gap-2 rounded-lgr outline-offset-4"
                :class="isOverDropZone ? 'outline outline-2 outline-button-primary' : ''">
                <label for="work-document-file" class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">Choose a PDF or image</span>
                    <input
                        id="work-document-file"
                        ref="fileInput"
                        type="file"
                        :accept="acceptedFileTypes"
                        :disabled="isBusy"
                        class="body-small text-control-text rounded-lgr border border-control-border bg-control-bg p-3 file:mr-3 file:rounded-lg file:border-0 file:bg-button-secondary file:px-3 file:py-2 file:text-button-secondary-text"
                        @change="handleFileChange" />
                </label>
                <p class="details text-ink-muted">PDF or still PNG, JPEG, WebP or GIF — drop a file here or browse. You can also paste a screenshot into your message.</p>
            </div>

            <BaseCheckbox
                v-if="allowAdvanced && capabilities && !capabilitiesError && !isLoadingCapabilities && !analysis && !isReading"
                v-model="isAdvanced"
                checkbox-id="work-document-advanced"
                shape="square"
                :disabled="isBusy"
                aria-describedby="work-document-advanced-description">
                <span class="body-small-semibold text-ink">Advanced options</span>
                <span id="work-document-advanced-description" class="details text-ink-muted mt-1 block"
                    >Customize the reading and refine the extracted content before adding it.</span
                >
            </BaseCheckbox>

            <BaseNotification v-if="isOfficeDocument && officeGuidance" theme="warning" hide-dismiss>
                <template #content>
                    <div>
                        <p class="body-small-semibold">{{ officeGuidance.title }}</p>
                        <p class="body-small mt-1">{{ officeGuidance.message }}</p>
                        <p class="details mt-2">PowerPoint: File → Export → PDF. Google Slides: File → Download → PDF. Keynote: File → Export To → PDF.</p>
                    </div>
                </template>
            </BaseNotification>

            <div aria-live="polite" aria-atomic="true">
                <div v-if="isInspecting" class="flex items-center gap-3 text-ink">
                    <LoadingSpinner class="h-5" />
                    <span class="body-small-semibold">Checking file…</span>
                </div>
                <BaseNotification v-else-if="inspectionError" theme="error" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ inspectionError }}</p>
                    </template>
                </BaseNotification>
            </div>

            <template v-if="inspection">
                <div class="flex flex-col gap-3">
                    <div class="flex items-start justify-between gap-3">
                        <p class="body-small text-ink min-w-0 break-words">
                            <span class="body-small-semibold">{{ inspection.filename }}</span>
                            · {{ inspectedFileDescription }} · {{ formattedSelectedFileSize }}
                        </p>
                        <BaseButton v-if="analysis" theme="tertiary" size="sm" aria-label="Clear this document" :disabled="isBusy" @click="clearAnalysis"> Clear </BaseButton>
                    </div>
                    <div v-if="previewBitmap" v-show="!isReading && !analysis" class="flex justify-center">
                        <canvas ref="imagePreviewCanvas" role="img" aria-label="Preview of selected image" class="fs-exclude block max-h-48 max-w-full rounded-lgr bg-surface" />
                    </div>
                </div>

                <div v-if="isAdvanced && !analysis && !isReading" class="flex flex-col gap-3">
                    <label for="work-document-focus" class="flex flex-col gap-2">
                        <span class="body-small-semibold text-ink">Reading instruction (optional)</span>
                        <BaseTextarea
                            id="work-document-focus"
                            v-model:input="focusInstruction"
                            aria-label="Reading instruction"
                            :disabled="isBusy"
                            placeholder="Leave blank for a general reading, or enter a specific request."
                            rows="3"
                            class="rounded-lgr border border-control-border bg-control-bg p-3" />
                    </label>
                    <p class="details text-ink-muted">For example: highlight goals and owners, compare metrics or groups, or focus on pages 4–6.</p>
                </div>
            </template>

            <div aria-live="polite" aria-atomic="true">
                <BaseNotification v-if="documentCheck || analysis" :theme="documentCheck ? (documentCheck.status === 'uncertain' ? 'warning' : 'error') : 'success'" hide-dismiss>
                    <template #content>
                        <div>
                            <p class="body-small-semibold">{{ checkResultTitle }}</p>
                            <template v-if="documentCheck">
                                <p class="body-small mt-2 whitespace-pre-wrap">{{ documentCheck.user_message }}</p>
                                <ul v-if="checkConcerns.length" class="details mt-2 list-disc pl-5">
                                    <li v-for="concern in checkConcerns" :key="concern.key">
                                        {{ concern.message }}
                                        <span v-if="concern.pages">({{ concern.pages }})</span>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </template>
                </BaseNotification>
                <div v-if="isAdvanced && checkInputTokens !== null && documentCheck?.status !== 'blocked'" class="mt-2 text-ink-muted">
                    <div class="flex items-center gap-2">
                        <StatusChip state="neutral" size="2xs">{{ checkInputTokenLabel }}</StatusChip>
                        <BasePopover as-child side="top" align="start" :side-offset="6" :collision-padding="12" content-class="max-w-80 p-3">
                            <template #trigger>
                                <BaseButton theme="tertiary" size="sm" icon-only class="text-ink-muted" aria-label="About visual document input tokens">
                                    <InfoIcon class="h-4 w-4" aria-hidden="true" />
                                </BaseButton>
                            </template>
                            <template #content>
                                <div class="details flex flex-col gap-2">
                                    <p>Tokens measure how much content was processed when checking this file, including text and visuals.</p>
                                    <p>This is separate from the reviewed content added to your chat, shown below.</p>
                                </div>
                            </template>
                        </BasePopover>
                    </div>
                </div>
            </div>

            <div>
                <div v-if="isReading || isRefining" class="flex flex-col gap-3 rounded-lgr border border-control-border bg-surface p-4">
                    <div aria-live="polite" aria-atomic="true" class="flex items-center gap-3 text-ink">
                        <LoadingSpinner class="h-5" />
                        <span class="body-small-semibold">{{ readingStatus }}</span>
                    </div>
                    <div
                        ref="liveReading"
                        role="region"
                        aria-label="Document reading progress"
                        tabindex="0"
                        class="overflow-y-auto overscroll-contain rounded-lgr bg-surface-sunken p-3"
                        :class="isAdvanced ? 'max-h-60 min-h-24' : 'h-24'">
                        <p v-if="livePreview" class="body-small text-ink-muted whitespace-pre-wrap">{{ livePreview }}</p>
                        <p v-else class="body-small text-ink-muted">Preparing the content for your conversation…</p>
                    </div>
                    <p v-if="!isAdvanced" class="details text-ink-muted">The content will be attached to your message when ready.</p>
                </div>
                <BaseNotification v-else-if="analysisError" theme="error" hide-dismiss>
                    <template #content>
                        <p class="body-small-semibold">{{ analysisError }}</p>
                    </template>
                </BaseNotification>
            </div>

            <div v-if="analysis && isAdvanced && !isRefining" class="flex flex-col gap-4 rounded-lgr border border-control-border bg-surface p-4">
                <div>
                    <h3 ref="analysisHeading" tabindex="-1" class="body-small-semibold text-ink">Ready to add</h3>
                    <div aria-live="polite" aria-atomic="true">
                        <p v-if="refinementCompleted" class="body-small-semibold text-ink mt-1">Content updated. Review it before adding it to chat.</p>
                    </div>
                    <BaseCollapsible v-model:open="isPreviewOpen" class="mt-2">
                        <template #trigger>
                            <span class="body-small text-ink flex items-center gap-2">
                                <i :class="isPreviewOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" aria-hidden="true"></i>
                                Preview extracted content
                            </span>
                        </template>
                        <template #content>
                            <div role="region" aria-label="Extracted document content" tabindex="0" class="mt-2 max-h-60 overflow-y-auto overscroll-contain pr-2 md:max-h-72">
                                <p class="body-small text-ink whitespace-pre-wrap">{{ analysis.summary }}</p>
                            </div>
                        </template>
                    </BaseCollapsible>
                    <BaseCollapsible v-if="analysis.limitations.length" class="mt-3" trigger-aria-label="Toggle reading limitations">
                        <template #trigger>
                            <span class="body-small-semibold text-ink underline">Reading limitations ({{ analysis.limitations.length }})</span>
                        </template>
                        <template #content>
                            <BaseNotification theme="info" hide-dismiss class="mt-2">
                                <template #content>
                                    <ul class="body-small list-disc pl-5">
                                        <li v-for="(limitation, index) in analysis.limitations" :key="index">{{ limitation }}</li>
                                    </ul>
                                </template>
                            </BaseNotification>
                        </template>
                    </BaseCollapsible>
                </div>
            </div>

            <div v-if="analysis && isRefinementOpen && !isRefining" id="work-document-refinement-panel" class="flex flex-col gap-3">
                <label for="work-document-refinement" class="flex flex-col gap-2">
                    <span class="body-small-semibold text-ink">Refinement instruction</span>
                    <BaseTextarea
                        id="work-document-refinement"
                        ref="refinementInput"
                        v-model:input="refinementInstruction"
                        aria-label="Refine this content"
                        :aria-describedby="refinementError ? 'work-document-refinement-error' : undefined"
                        :disabled="isBusy"
                        rows="3"
                        class="rounded-lgr border border-control-border bg-control-bg p-3"
                        placeholder="For example: Expand the metrics and action items." />
                </label>
                <div aria-live="polite" aria-atomic="true">
                    <BaseNotification v-if="refinementError" theme="error" hide-dismiss>
                        <template #content>
                            <div id="work-document-refinement-error">
                                <p class="body-small-semibold">{{ refinementError }}</p>
                                <p class="details mt-1">The previous content is unchanged.</p>
                            </div>
                        </template>
                    </BaseNotification>
                </div>
                <p class="details text-ink-muted">Refinement re-reads the original document with this request, the current reading, and recent modal history.</p>
            </div>
        </div>

        <template v-if="inspection" #footer>
            <div class="flex w-full flex-col gap-3">
                <div class="flex items-center gap-2">
                    <p class="details text-ink-muted">
                        Only the extracted content—not the original file—will be added to chat<span v-if="analysis" class="font-mono">
                            ({{ projectedChatContextValue }} tokens)</span
                        >.
                    </p>
                    <BasePopover as-child side="top" align="end" :side-offset="6" :collision-padding="12" content-class="max-w-80 p-3">
                        <template #trigger>
                            <BaseButton theme="tertiary" size="sm" icon-only aria-label="How visual document data is handled">
                                <InfoIcon class="h-4 w-4" aria-hidden="true" />
                            </BaseButton>
                        </template>
                        <template #content>
                            <div class="details flex flex-col gap-2">
                                <p>Your file is processed for checking, reading, and any refinement. The original file is not retained in Nadia’s chat or processing logs.</p>
                                <p>
                                    Derived text and summaries are saved in processing logs. The attachment stays in your browser until you send your message. Removing the chip
                                    discards that draft attachment; processing logs remain.
                                </p>
                            </div>
                        </template>
                    </BasePopover>
                </div>

                <BaseNotification
                    v-if="!props.canAdd"
                    theme="warning"
                    hide-dismiss
                    :text="
                        isAdvanced
                            ? 'Remove an attachment before adding this content to chat.'
                            : 'You’ve reached the attachment limit. Close this, remove an attachment, then read the document again.'
                    " />
                <div v-if="isAdvanced || (!isBusy && (documentCheck || analysisError))" class="flex flex-wrap items-center justify-between gap-3">
                    <template v-if="!analysis">
                        <BaseButton theme="neutral" :disabled="isBusy" @click="clearAnalysis">{{ isAdvanced ? "Previous" : "Choose another file" }}</BaseButton>
                        <BaseButton v-if="!documentCheck || documentCheck.status !== 'blocked'" theme="primary" :disabled="!canRead" @click="readDocument">{{
                            isAdvanced ? "Read" : "Try again"
                        }}</BaseButton>
                    </template>
                    <template v-else-if="isRefinementOpen">
                        <BaseButton theme="secondary" :disabled="isBusy" @click="cancelRefinement">Cancel refinement</BaseButton>
                        <BaseButton theme="primary" :disabled="!canRefine" @click="refineDocument">{{ isRefining ? "Updating…" : "Update content" }}</BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton v-if="isAdvanced" theme="secondary" aria-controls="work-document-refinement-panel" :disabled="isBusy" @click="startRefinement"
                            >Refine</BaseButton
                        >
                        <BaseButton theme="primary" class="ml-auto" :disabled="isBusy || !props.canAdd" @click="addToChat">Add content to chat</BaseButton>
                    </template>
                </div>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useDropZone } from "@vueuse/core";
import visualDocumentTutorial from "~img/visual-document-tutorial.gif";
import { useFetch } from "~vue/api";
import type { VisualDocumentContent } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import InfoIcon from "~vue/icons/InfoIcon.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, useTemplateRef, watch } from "vue";

import { streamWorkDocument } from "./workDocumentStream";
import type { WorkDocumentAnalysisResponse, WorkDocumentCapabilities, WorkDocumentCheckResponse, WorkDocumentInspectionResponse } from "./workDocumentTypes";

const props = withDefaults(defineProps<{ capabilitiesUrl: string; canAdd?: boolean; allowAdvanced?: boolean }>(), { canAdd: true, allowAdvanced: false });
const emit = defineEmits<{ added: [VisualDocumentContent]; "upload-error": [string] }>();
const isOpen = defineModel<boolean>("open", { default: false });
const pendingFile = defineModel<File | null>("file", { default: null });

const selectedFile = ref<File | null>(null);
const isAdvanced = ref(false);
const isPreviewOpen = ref(false);
const focusInstruction = ref("");
const inspection = ref<WorkDocumentInspectionResponse | null>(null);
const inspectionError = ref("");
const documentCheck = ref<WorkDocumentCheckResponse | null>(null);
const checkInputTokens = ref<number | null>(null);
const analysis = ref<WorkDocumentAnalysisResponse | null>(null);
const analysisError = ref("");
const operation = ref<"inspect" | "read" | "refine" | null>(null);
const refinementInstruction = ref("");
const refinementError = ref("");
const refinementCompleted = ref(false);
const modalHistory = ref<string[]>([]);
const isRefinementOpen = ref(false);
const refinementInput = ref<InstanceType<typeof BaseTextarea> | null>(null);
const analysisHeading = ref<HTMLElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const fileDropZone = useTemplateRef("fileDropZone");
const imagePreviewCanvas = ref<HTMLCanvasElement | null>(null);
const liveReading = ref<HTMLElement | null>(null);
const previewBitmap = shallowRef<ImageBitmap | null>(null);
const readingPhase = ref<"checking" | "reading" | "refining" | null>(null);
const livePreview = ref("");
let streamController: AbortController | null = null;
let stateVersion = 0;

const {
    data: capabilities,
    error: capabilitiesError,
    isFetching: isLoadingCapabilities,
    execute: fetchCapabilities,
} = useFetch(props.capabilitiesUrl, { immediate: false }).get().json<WorkDocumentCapabilities>();

const acceptedFileTypes = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.ppt,.pptx";
const officeGuidance = computed(() => capabilities.value?.office_document_guidance);
const isOfficeDocument = computed(() => /\.pptx?$/i.test(selectedFile.value?.name ?? ""));
const isBusy = computed(() => isLoadingCapabilities.value || operation.value !== null);
const isInspecting = computed(() => operation.value === "inspect");
const isReading = computed(() => operation.value === "read");
const isRefining = computed(() => operation.value === "refine");
const progressSteps = computed(() => [{ id: 1, label: "Choose document" }, { id: 2, label: "Read" }, ...(isAdvanced.value ? [{ id: 3, label: "Review" }] : [])]);
const currentStage = computed(() => (analysis.value && isAdvanced.value ? 3 : inspection.value ? 2 : 1));
const canNavigateToStep = (step: number) => currentStage.value === 2 && !analysis.value && step === 1 && !isBusy.value;
const navigateToStep = (step: number) => {
    if (canNavigateToStep(step)) {
        void clearAnalysis();
    }
};
const canRead = computed(() => props.canAdd && !!selectedFile.value && !!inspection.value && !!capabilities.value && !isOfficeDocument.value && !isBusy.value);
const canRefine = computed(() => isAdvanced.value && !!analysis.value && !!selectedFile.value && !!refinementInstruction.value.trim() && !isBusy.value);
const checkResultTitle = computed(() =>
    documentCheck.value
        ? documentCheck.value.status === "blocked"
            ? "This document contains content that violates our usage policy"
            : "We couldn’t fully review this upload"
        : "Checks passed",
);
const checkConcerns = computed(() => {
    const check = documentCheck.value;
    if (!check) {
        return [];
    }
    const concerns = check.findings
        .filter((finding) => finding.status !== "clear")
        .map((finding) => ({
            key: finding.key,
            message: finding.user_reason,
            pages: [...new Set(finding.evidence.flatMap((item) => (item.page === null ? [] : [item.page])))].map((page) => `p. ${page}`).join(", "),
        }));
    if (check.prompt_injection_detected) {
        concerns.unshift({ key: "prompt-injection", message: check.prompt_injection_user_message || check.user_message, pages: "" });
    }
    [...(check.safety_coverage?.limitations ?? []), ...(check.limitations ?? [])].forEach((message, index) => {
        if (message && !concerns.some((concern) => concern.message === message)) {
            concerns.push({ key: `coverage-${index}`, message, pages: "" });
        }
    });
    return concerns.filter((concern) => concern.message !== check.user_message);
});
const formattedSelectedFileSize = computed(() => {
    const bytes = selectedFile.value?.size ?? 0;
    return bytes >= 1024 * 1024 ? `${formatNumber(bytes / (1024 * 1024), "one-decimal")} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
});
const inspectedFileDescription = computed(() => {
    const pages = inspection.value?.page_count;
    return pages === null ? "1 image" : `${pages} ${pages === 1 ? "page" : "pages"}`;
});
const checkInputTokenLabel = computed(() => (checkInputTokens.value === null ? "Input tokens unavailable" : `${formatNumber(checkInputTokens.value, "compact")} input tokens`));
const projectedChatContextValue = computed(() => (analysis.value ? `~${formatNumber(analysis.value.context_token_count, "compact")}` : ""));
const readingStatus = computed(() =>
    readingPhase.value === "checking" ? "Checking the document…" : readingPhase.value === "refining" ? "Refining the extracted content…" : "Reading the document…",
);

const clearPreview = () => {
    previewBitmap.value?.close();
    previewBitmap.value = null;
};
const resetFileSelection = () => {
    stateVersion += 1;
    streamController?.abort();
    streamController = null;
    selectedFile.value = null;
    inspection.value = null;
    inspectionError.value = "";
    documentCheck.value = null;
    checkInputTokens.value = null;
    analysis.value = null;
    isPreviewOpen.value = false;
    analysisError.value = "";
    readingPhase.value = null;
    livePreview.value = "";
    operation.value = null;
    focusInstruction.value = "";
    refinementInstruction.value = "";
    refinementError.value = "";
    refinementCompleted.value = false;
    modalHistory.value = [];
    isRefinementOpen.value = false;
    clearPreview();
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};
watch(
    isOpen,
    (open) => {
        if (open) {
            fetchCapabilities();
        } else {
            pendingFile.value = null;
            resetFileSelection();
            isAdvanced.value = false;
        }
    },
    { immediate: true },
);
onBeforeUnmount(resetFileSelection);

const drawImagePreview = () => {
    if (!previewBitmap.value || !imagePreviewCanvas.value) {
        return;
    }
    const scale = Math.min(1, 800 / previewBitmap.value.width, 384 / previewBitmap.value.height);
    const canvas = imagePreviewCanvas.value;
    canvas.width = Math.round(previewBitmap.value.width * scale);
    canvas.height = Math.round(previewBitmap.value.height * scale);
    canvas.getContext("2d")?.drawImage(previewBitmap.value, 0, 0, canvas.width, canvas.height);
};
const selectFile = async (file: File | null) => {
    resetFileSelection();
    selectedFile.value = file;
    if (!file || isOfficeDocument.value || !capabilities.value) {
        return;
    }
    if (file.size > capabilities.value.max_source_file_bytes) {
        inspectionError.value = `This file is too large. Upload a file of ${Math.floor(capabilities.value.max_source_file_bytes / 1_000_000)} MB or less.`;
        return;
    }
    const requestVersion = stateVersion;
    operation.value = "inspect";
    const formData = new FormData();
    formData.append("file", file);
    const { data, error } = await useFetch(capabilities.value.inspect_url).post(formData).json<WorkDocumentInspectionResponse & { error?: string }>();
    if (requestVersion !== stateVersion) {
        return;
    }
    if (error.value || !data.value || data.value.error) {
        operation.value = null;
        inspectionError.value = data.value?.error ?? error.value?.message ?? "This file could not be checked. Try another file.";
        return;
    }
    // Inspect dimensions before decoding a browser preview of a potentially huge image.
    if (isAdvanced.value && file.type.startsWith("image/")) {
        try {
            const bitmap = await createImageBitmap(file);
            if (requestVersion !== stateVersion) {
                bitmap.close();
                return;
            }
            previewBitmap.value = bitmap;
        } catch {
            /* A preview is optional; the server has validated the file. */
        }
        if (requestVersion !== stateVersion) {
            return;
        }
    }
    operation.value = null;
    inspection.value = data.value;
    await nextTick();
    drawImagePreview();
    if (!isAdvanced.value && requestVersion === stateVersion) {
        await readDocument();
    }
};
const handleFileChange = (event: Event) => selectFile((event.target as HTMLInputElement).files?.[0] ?? null);
const { isOverDropZone } = useDropZone(fileDropZone, {
    onDrop: (files) => {
        if (files?.length && !isBusy.value) {
            void selectFile(files[0]);
            if (files.length > 1) {
                emit("upload-error", "Only the first dropped file was processed — drop one file at a time.");
            }
        }
    },
    preventDefaultForUnhandled: true,
});
const showReading = async () => {
    await nextTick();
    analysisHeading.value?.focus({ preventScroll: true });
    analysisHeading.value?.scrollIntoView({ block: "start" });
};
const processDocument = async (refinement: boolean) => {
    if (!selectedFile.value || !capabilities.value || isBusy.value) {
        return;
    }
    const requestVersion = stateVersion;
    const instruction = refinementInstruction.value.trim();
    const formData = new FormData();
    formData.append("file", selectedFile.value);
    formData.append("focus_instruction", isAdvanced.value ? focusInstruction.value.trim() : "");
    if (refinement && analysis.value) {
        formData.append("current_reading", analysis.value.summary);
        formData.append("refinement_instruction", instruction);
        formData.append("modal_history", JSON.stringify(modalHistory.value));
    }
    operation.value = refinement ? "refine" : "read";
    readingPhase.value = "checking";
    livePreview.value = "";
    refinementError.value = "";
    analysisError.value = "";
    if (!refinement) {
        documentCheck.value = null;
        analysis.value = null;
    }
    const url = refinement ? capabilities.value.refine_url : capabilities.value.read_url;
    streamController = new AbortController();
    const streamResult: { value: WorkDocumentAnalysisResponse | null } = { value: null };
    let failed = false;
    try {
        await streamWorkDocument(
            url,
            formData,
            (event) => {
                if (requestVersion !== stateVersion) {
                    return;
                }
                if (event.type === "phase") {
                    readingPhase.value = event.phase;
                } else if (event.type === "preview") {
                    livePreview.value = event.text;
                    nextTick(() => {
                        if (liveReading.value) {
                            liveReading.value.scrollTop = liveReading.value.scrollHeight;
                        }
                    });
                } else if (event.type === "complete") {
                    streamResult.value = event.result;
                    livePreview.value = event.result.summary;
                } else if (event.type === "error") {
                    failed = true;
                    if (refinement) {
                        refinementError.value = event.error;
                    } else {
                        documentCheck.value = event.check ?? null;
                        checkInputTokens.value = event.check_input_tokens ?? null;
                        analysisError.value = documentCheck.value ? "" : event.error;
                    }
                    livePreview.value = "";
                }
            },
            streamController.signal,
        );
    } catch (error) {
        if (requestVersion !== stateVersion || (error instanceof DOMException && error.name === "AbortError")) {
            return;
        }
        failed = true;
        livePreview.value = "";
        const message = error instanceof Error ? error.message : "The document could not be read. Try again.";
        if (refinement) {
            refinementError.value = message;
        } else {
            analysisError.value = message;
        }
    } finally {
        if (requestVersion === stateVersion) {
            operation.value = null;
            readingPhase.value = null;
            streamController = null;
        }
    }
    if (requestVersion !== stateVersion || failed) {
        return;
    }
    const completedResult = streamResult.value;
    if (!completedResult) {
        const message = "The document reading ended before it was complete. Try again.";
        if (refinement) {
            refinementError.value = message;
        } else {
            analysisError.value = message;
        }
        return;
    }
    analysis.value = completedResult;
    isPreviewOpen.value = false;
    documentCheck.value = null;
    checkInputTokens.value = completedResult.check_input_tokens;
    if (refinement) {
        modalHistory.value.push(instruction);
        refinementInstruction.value = "";
        isRefinementOpen.value = false;
        refinementCompleted.value = true;
    }
    if (isAdvanced.value) {
        await showReading();
    } else {
        addToChat();
    }
};
const readDocument = () => {
    if (canRead.value) {
        return processDocument(false);
    }
};
const refineDocument = () => {
    if (canRefine.value) {
        return processDocument(true);
    }
};
const startRefinement = async () => {
    refinementCompleted.value = false;
    isRefinementOpen.value = true;
    await nextTick();
    refinementInput.value?.textareaRef?.focus();
};
const cancelRefinement = async () => {
    refinementInstruction.value = "";
    refinementError.value = "";
    isRefinementOpen.value = false;
    await showReading();
};
const clearAnalysis = async () => {
    resetFileSelection();
    await nextTick();
    fileInput.value?.focus();
};
const addToChat = () => {
    if (!analysis.value || !selectedFile.value || !props.canAdd || isBusy.value || isRefinementOpen.value) {
        return;
    }
    emit("added", {
        kind: "visual_document",
        draftId: crypto.randomUUID(),
        name: selectedFile.value.name,
        type: selectedFile.value.name.split(".").pop()?.toLowerCase() ?? "",
        content: analysis.value.context_text,
        description: "",
        limitations: analysis.value.limitations,
    });
    isOpen.value = false;
};

watch(isAdvanced, (advanced) => {
    if (!advanced && canRead.value && !analysis.value) {
        void readDocument();
    }
});

watch(
    [isOpen, pendingFile, capabilities, isLoadingCapabilities, capabilitiesError],
    ([open, file, loaded, loading, error]) => {
        if (open && file && loaded && !loading && !error) {
            pendingFile.value = null;
            void selectFile(file);
        }
    },
    { immediate: true },
);
</script>
